// The stored array is the conversation sequence. Device clocks in legacy
// messages can go backwards, so timestamps must never reorder that sequence.
export function toDateValue(value) {
  if (value === null || value === undefined || value === '') return null;
  let date;
  try {
    if (value instanceof Date) date = value;
    else if (typeof value?.toDate === 'function') date = value.toDate();
    else if (typeof value?.toMillis === 'function') date = new Date(value.toMillis());
    else if (typeof value === 'object' && Number.isFinite(Number(value.seconds))) {
      date = new Date(Number(value.seconds) * 1000 + Number(value.nanoseconds || 0) / 1e6);
    } else date = new Date(value);
  } catch {
    return null;
  }
  return date instanceof Date && Number.isFinite(date.getTime()) ? date : null;
}

export function buildTechnicalMessageKey(message, fallbackIndex = 0) {
  if (!message || typeof message !== 'object') return `idx:${fallbackIndex}`;
  if (message.id) return `message:${message.id}`;
  return JSON.stringify([
    message.type || '', message.userId || '',
    toDateValue(message.timestamp)?.toISOString() || String(message.timestamp ?? ''),
    message.content || '',
  ]);
}

export function getTicketLastActivityMs(ticket = {}) {
  const messages = Array.isArray(ticket.conversations) ? ticket.conversations : [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const date = toDateValue(messages[index]?.timestamp);
    if (date) return date.getTime();
  }
  return toDateValue(ticket.updatedAt)?.getTime()
    ?? toDateValue(ticket.timestamp)?.getTime()
    ?? toDateValue(ticket.createdAt)?.getTime()
    ?? 0;
}

export function mergeTechnicalConversation(conversations = [], documents = []) {
  const messages = (Array.isArray(conversations) ? conversations : []).map((message, index) => ({
    ...message,
    source: 'conversation',
    conversationIndex: index,
    messageKey: `${buildTechnicalMessageKey(message, index)}:${index}`,
  }));
  // Monotonic anchors place attachments in the timeline without moving any
  // message, even when a legacy message has an earlier device timestamp.
  let anchor = 0;
  const anchors = messages.map(message => {
    anchor = Math.max(anchor, toDateValue(message.timestamp)?.getTime() || 0);
    return anchor;
  });
  const buckets = Array.from({ length: messages.length + 1 }, () => []);
  const attachments = (Array.isArray(documents) ? documents : []).map(document => ({
    ...document,
    type: 'Document',
    timestamp: document.uploadedAt,
    source: 'document',
    messageKey: `document:${document.id || document.fileUrl}`,
  })).sort((a, b) => (toDateValue(a.timestamp)?.getTime() || Infinity)
    - (toDateValue(b.timestamp)?.getTime() || Infinity));
  for (const document of attachments) {
    const millis = toDateValue(document.timestamp)?.getTime();
    const nextMessage = millis === undefined ? -1 : anchors.findIndex(time => time > millis);
    buckets[nextMessage === -1 ? messages.length : nextMessage].push(document);
  }
  const merged = [];
  messages.forEach((message, index) => merged.push(...buckets[index], message));
  merged.push(...buckets[messages.length]);
  return merged;
}
